import React, { useRef, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import SideNavbar from '../../../components/SideNavbar';
import StaffModule from '../../../components/CallCRM/staffTopModule';
import OutboundCalls from '../../../components/CallCRM/OutboundCalls';
import TotalCallDuration from '../../../components/CallCRM/TotalCallDuration';
import ConnectionRatio from '../../../components/CallCRM/ConnectionRatio';
import LeadAnalytics from '../../../components/CallCRM/LeadAnalytics';
import StageTimelineAnalytics from '../../../components/CallCRM/LeadStageAnalytics';
import AdmissionAnalytics from '../../../components/CallCRM/AdmissionAnalytics';
import CampaignDistribution from '../../../components/CallCRM/CampaignDistribution';
import ChannelAnalytics from '../../../components/CallCRM/ChannelAnalytics';
import BillingAnalytics from '../../../components/CallCRM/BillingAnalytics';
import ConversionRatio from "../../../components/ConversionRatio";
import CallDistribution from "../../../components/CallDistribution";

// Firebase
import {
    doc,
    getDoc,
    collection,
    query,
    where,
    getDocs,
    Timestamp,
    documentId,
    DocumentData,
    Query
} from 'firebase/firestore';
import { db, auth } from "../../../firebaseConfig";

// global
import { useSubAgentAuth } from '../SubAgent/SubAgentAuthContext';

// redux
import { useAppSelector } from '../../../redux/store';
import { FirebaseError } from 'firebase/app';
import { useNavigate } from 'react-router-dom';
import getCurrentScreen from '../../../Helper/GetScreen';
import { useAuth } from '../../../AuthContext';

interface UserOption {
    id: string;
    name: string;
}

const SubAgentDashboard = () => {

    const navigate = useNavigate();

    const { currentUser, isAdmin, isManager, isStaff } = useAuth();
    // Redux state
    const isCollapsed = useAppSelector((state) => state.sidebar.isCollapsed);
    const currentScreen = getCurrentScreen(useAppSelector(state => state.screen.currentScreen), isAdmin, isManager, isStaff);

    // Auth context
    const { currentSubAgent, subAgentData } = useSubAgentAuth();


    // Component state
    const [users, setUsers] = React.useState<UserOption[]>([]);
    const [selectedUser, setSelectedUser] = React.useState<UserOption[]>([]);
    const [searchQuery, setSearchQuery] = React.useState('');

    // Date picker state
    const [customDatePopupOpen, setCustomDatePopupOpen] = React.useState(false);
    const [startDate, setStartDate] = React.useState<Date | null>(null);
    const [endDate, setEndDate] = React.useState<Date | null>(null);
    const [filter, setFilter] = React.useState<boolean>(false);
    const customDatePopupRef = useRef<HTMLDivElement>(null);

    // Settings state
    interface SettingOption {
        id: string;
        [key: string]: any;
    }

    const [settings, setSettings] = React.useState<{
        stages: SettingOption[];
        campaigns: SettingOption[];
        sources: SettingOption[];
        statuses: SettingOption[];
        admissionStatus: SettingOption[];
    }>({
        stages: [],
        campaigns: [],
        sources: [],
        statuses: [],
        admissionStatus: []
    });

    // Close popup when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (customDatePopupRef.current && !customDatePopupRef.current.contains(event.target as Node)) {
                setCustomDatePopupOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                // Check if companyId exists
                if (!subAgentData?.companyId) {
                    console.warn('No company ID found');
                    navigate('/');
                    return;
                }

                const stageRef = collection(db, `companies/${subAgentData.companyId}/leadStages`);
                const campaignRef = collection(db, `companies/${subAgentData.companyId}/leadCampaigns`);
                const sourceRef = collection(db, `companies/${subAgentData.companyId}/leadSources`);
                const statusRef = collection(db, `companies/${subAgentData.companyId}/leadStatuses`);
                const admissionStatusRef = collection(db, `companies/${subAgentData.companyId}/leadAdmissionStatus`);

                const fetchCollectionDocs = async (ref: Query<unknown, DocumentData>) => {
                    const snapshot = await getDocs(ref);
                    return snapshot.docs.map(doc => ({ id: doc.id, ...(doc.data() as Record<string, unknown>) }));
                };

                const [stages, campaigns, sources, statuses, admissionStatus] = await Promise.all([
                    fetchCollectionDocs(stageRef),
                    fetchCollectionDocs(campaignRef),
                    fetchCollectionDocs(sourceRef),
                    fetchCollectionDocs(statusRef),
                    fetchCollectionDocs(admissionStatusRef)
                ]);

                setSettings({ stages, campaigns, sources, statuses, admissionStatus });
            } catch (error) {
                console.error("Error fetching settings:", error);

                // Handle insufficient permissions
                if (error instanceof FirebaseError && error.code === 'permission-denied') {
                    console.warn("Insufficient permissions to fetch settings");
                    navigate('/');
                    return;
                }

                // Reset settings or handle error as needed
                setSettings({
                    stages: [],
                    campaigns: [],
                    sources: [],
                    statuses: [],
                    admissionStatus: []
                });
            }
        };

        // Only fetch if companyId exists
        if (subAgentData?.companyId) {
            fetchSettings();
        }
    }, [subAgentData?.companyId, navigate]);

    const fetchAssignedToOptions = React.useCallback(async () => {
        setSelectedUser([]);
        if (!subAgentData?.companyId) return;
        try {

            let subAgentQuery = query(
                collection(db, "companies", subAgentData?.companyId, "subAgents"),
                where(documentId(), "==", currentSubAgent?.uid)
            );

            const usersSnapshot = await getDocs(subAgentQuery);
            const users = usersSnapshot.docs.map((doc) => ({
                id: doc.id,
                name: doc.data().name
            }));
            setUsers(users);
        } catch (error) {
            console.error("Error fetching users:", error);
        }
    }, [subAgentData?.companyId, currentScreen, currentSubAgent?.uid]);

    React.useEffect(() => {
        fetchAssignedToOptions();
    }, [fetchAssignedToOptions]);

    const filteredUsers = users.filter(user =>
        user.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleSelectedUser = (e: React.ChangeEvent<HTMLSelectElement>) => {
        if (e.target.value === 'All Staff') return setSelectedUser([]);
        setSelectedUser([{ id: e.target.value, name: '' }]);
    };

    const handleDateRangeClick = (e: React.MouseEvent) => {
        e.preventDefault();
        setCustomDatePopupOpen(true);
    };

    useEffect(() => {
        setStartDate(new Date());
        setEndDate(new Date());
    }, [subAgentData?.companyId, users, currentScreen]);

    const handleDateRange = (date: Date | null, type: string) => {
        if (!date) return null;

        if (type === 'start') {
            date.setHours(5, 30, 0, 0);
        } else {
            // Add one day for end date
            const nextDay = new Date(date);
            nextDay.setDate(nextDay.getDate() + 1);
            nextDay.setHours(5, 29, 59, 0);
            return nextDay.toISOString();
        }

        return date.toISOString();
    };

    const handleApplyFilters = () => {
        setFilter(true);
        setCustomDatePopupOpen(false);
    };

    return (
        <div className="min-h-screen bg-gray-200">
            {/* Left Sidebar */}
            <div className="fixed top-16 left-0 h-full z-10">
                <SideNavbar />
            </div>

            {/* Main Content */}
            <div className={`${isCollapsed ? 'ml-[70px]' : 'ml-[290px]'} mt-[110px] p-4 lg:p-8 transition-all duration-300`}>

                {/* {currentScreen === 'Staff' && (
                    <div className='mb-3'>
                        <StaffModule />
                    </div>
                )} */}

                {/* Top Header Section */}
                {/* <div className="bg-white p-4 lg:p-6 rounded-3xl shadow-md mb-3">
                    <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between mb-6 gap-4">
                        <div className="flex items-center gap-4">
                            <div className="flex items-center justify-center w-10 h-10 rounded-full"
                                style={{ background: "linear-gradient(180deg, #00A6D9 0%, #3483BE 100%)" }}>
                                <img src={`${process.env.PUBLIC_URL}/images/desktop/call-dropped.svg`}
                                    alt="Icon" className="w-6 h-6" />
                            </div>
                            <div className="flex items-center">
                                <h2 className="text-lg lg:text-xl text-black font-medium"
                                    style={{ fontFamily: "Montserrat", lineHeight: "29.26px" }}>
                                    Call CRM
                                </h2>
                            </div>
                        </div>
                        
                        <div className="flex items-center gap-4">
                            {currentScreen !== 'Staff' && (
                                <select
                                    className="w-full sm:w-auto p-2 border border-cyan-600 text-sm rounded-lg bg-white"
                                    value={selectedUser[0]?.id}
                                    onChange={handleSelectedUser}>
                                    <option>All Staff</option>
                                    {filteredUsers.map(user => (
                                        <option key={user.id} value={user.id}>{user.name}</option>
                                    ))}
                                </select>
                            )}

                            <div className="relative">
                                <button
                                    className="w-full sm:w-auto p-2 border border-cyan-600 text-sm rounded-lg bg-white text-left"
                                    onClick={handleDateRangeClick}
                                >
                                    {startDate && endDate
                                        ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
                                        : "Select Date Range"}
                                </button>

                                {customDatePopupOpen && (
                                    <div
                                        ref={customDatePopupRef}
                                        className="absolute top-full right-0 mt-2 w-auto h-auto bg-white border rounded-3xl shadow-lg p-4 z-[5]"
                                    >
                                        <div className="flex justify-between items-center mb-2">
                                            <h3 className="text-[14px] font-normal leading-[20px] text-left">
                                                Select Date Range
                                            </h3>
                                        </div>
                                        <div className="mb-1">
                                            <span className="text-[12px] font-normal leading-[16px] text-left">
                                                <span className='font-medium'>Current: </span>
                                                {startDate && endDate
                                                    ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
                                                    : "Select Date Range"}
                                            </span>
                                            <div className="grid grid-cols-2 gap-2 mt-2">
                                                <button onClick={() => { setStartDate(new Date()); setEndDate(new Date()); }} className="text-[12px] font-normal leading-[16px] text-left text-cyan-600">Today</button>
                                                <button onClick={() => { setStartDate(new Date(new Date().setDate(new Date().getDate() - 7))); setEndDate(new Date()); }} className="text-[12px] font-normal leading-[16px] text-left text-cyan-600">Last 7 Days</button>
                                                <button onClick={() => { setStartDate(new Date(new Date().setDate(new Date().getDate() - 30))); setEndDate(new Date()); }} className="text-[12px] font-normal leading-[16px] text-left text-cyan-600">Last 30 Days</button>
                                                <button onClick={() => { setStartDate(new Date(new Date().setDate(new Date().getDate() - 90))); setEndDate(new Date()); }} className="text-[12px] font-normal leading-[16px] text-left text-cyan-600">Last 90 Days</button>
                                            </div>
                                            <div
                                                className="p-4 bg-white shadow-lg rounded-[12px]"
                                                style={{ width: '100%', height: 'auto' }}
                                            >
                                                <DatePicker
                                                    selected={startDate}
                                                    startDate={startDate || undefined}
                                                    endDate={endDate || undefined}
                                                    selectsRange
                                                    onChange={(update) => {
                                                        const [start, end] = update;
                                                        setStartDate(start);
                                                        setEndDate(end);
                                                    }}
                                                    inline
                                                    className="w-full h-full p-4 bg-gray-100 shadow-lg rounded-[12px] custom-calendar"
                                                    maxDate={new Date()}
                                                />
                                            </div>
                                        </div>
                                        <div className="flex justify-between mt-2">
                                            <button
                                                className="px-8 py-2 text-white bg-black rounded-full"
                                                onClick={() => setCustomDatePopupOpen(false)}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                className="px-8 py-2 text-white bg-cyan-600 rounded-full"
                                                onClick={handleApplyFilters}
                                                disabled={!startDate || !endDate}
                                            >
                                                Apply
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        <div className="rounded-lg">
                            <OutboundCalls users={users} selectedUser={selectedUser} startTime={handleDateRange(startDate, 'start')} endTime={handleDateRange(endDate, 'end')} />
                        </div>
                        <div className="p-4 border border-cyan-600 rounded-lg">
                            <TotalCallDuration users={users} selectedUser={selectedUser} startTime={handleDateRange(startDate, 'start')} endTime={handleDateRange(endDate, 'end')} />
                        </div>
                        <div className="p-4 border border-cyan-600 rounded-lg">
                            <ConnectionRatio users={users} selectedUser={selectedUser} startTime={handleDateRange(startDate, 'start')} endTime={handleDateRange(endDate, 'end')} />
                        </div>
                    </div>
                </div> */}

                <div className="flex flex-col lg:flex-row gap-3 mb-3">
                    <div className="w-full lg:w-[60%] bg-white p-4 lg:p-6 rounded-3xl shadow-md">
                        <LeadAnalytics settings={settings} />
                    </div>
                    <div className="w-full lg:w-[40%] bg-white p-4 lg:p-6 rounded-3xl shadow-md">
                        <StageTimelineAnalytics settings={settings} />
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-1 gap-3 mb-3">
                    <div className="bg-white p-4 lg:p-6 rounded-3xl shadow-md">
                        <AdmissionAnalytics settings={settings} />
                    </div>
                    {/* {currentScreen !== 'Staff' && (
                        <div className="bg-white p-4 lg:p-6 rounded-3xl shadow-md">
                            <CampaignDistribution users={users} settings={settings} />
                        </div>
                    )} */}
                </div>

                {/* <div className="flex flex-col lg:flex-row gap-3">
                    {currentScreen !== 'Staff' && (
                        <div className="w-full lg:w-[60%] bg-white p-4 lg:p-6 rounded-3xl shadow-md">
                            <ChannelAnalytics users={users} settings={settings} />
                        </div>
                    )}
                    {currentScreen !== 'Staff' && currentScreen !== 'Manager' && (
                        <div className="w-full lg:w-[40%] bg-white p-4 lg:p-6 rounded-3xl shadow-md">
                            <BillingAnalytics />
                        </div>
                    )}
                </div> */}
            </div>
        </div>
    );
};

export default SubAgentDashboard;